<template>
  <div class="backimg"></div>

  <div class="page">
    <LoadingOverlay :show="isLoading" />

    <transition name="toast">
      <div
        v-if="showMessage && message"
        class="toast"
        role="status"
        aria-live="polite"
      >
        <div class="toast-inner">
          <div class="toast-dot"></div>
          <div class="toast-text">
            <div class="toast-title">Heads up</div>
            <div class="toast-msg">{{ message }}</div>
          </div>
          <button
            type="button"
            class="toast-close"
            @click="closeMessage"
            aria-label="Close notification"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      </div>
    </transition>

    <div class="card">
      <div class="card-top">
        <div class="brand">
          <img alt="TALKVOX logo" class="brand-logo" src="/assets/logo.png" />
        </div>

        <div class="title-wrap">
          <h1 class="title">Welcome back</h1>
          <p class="subtitle">Sign in to your TalkVox dashboard</p>
        </div>
      </div>

      <form @submit.prevent="performLogin" name="login-form" class="form">
        <div class="field">
          <label for="email" class="label">Email</label>
          <div class="control">
            <input
              :disabled="isLoggingIn"
              type="email"
              id="email"
              v-model="account.email"
              @blur="validateEmail"
              :class="emailError ? 'input input-error' : 'input'"
              @keyup.enter="performLogin"
              autocomplete="username"
              placeholder="you@company.com"
              aria-describedby="email-help"
              :aria-invalid="!!emailError"
            />
          </div>

          <p id="email-help" class="help" :class="emailError ? 'help-error' : 'help-muted'">
            <span v-if="emailError">{{ emailError }}</span>
            <span v-else>Use the email you signed up with.</span>
          </p>
        </div>

        <div class="field">
          <div class="label-row">
            <label for="password" class="label">Password</label>
            <a href="/forgot" class="link">Forgot password?</a>
          </div>

          <div class="control password-wrap">
            <input
              :disabled="isLoggingIn"
              :type="showPassword ? 'text' : 'password'"
              id="password"
              v-model="account.password"
              @blur="validatePassword"
              :class="passwordError ? 'input input-error pr-12' : 'input pr-12'"
              @keyup.enter="performLogin"
              autocomplete="current-password"
              placeholder="••••••••"
              aria-describedby="password-help"
              :aria-invalid="!!passwordError"
            />

            <button
              type="button"
              class="pw-toggle"
              @click="showPassword = !showPassword"
              :disabled="isLoggingIn"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
            >
              <svg v-if="!showPassword" viewBox="0 0 24 24" class="pw-icon" fill="none">
                <path
                  d="M2.25 12s3.75-7.5 9.75-7.5S21.75 12 21.75 12s-3.75 7.5-9.75 7.5S2.25 12 2.25 12Z"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M12 15.75A3.75 3.75 0 1 0 12 8.25a3.75 3.75 0 0 0 0 7.5Z"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

              <svg v-else viewBox="0 0 24 24" class="pw-icon" fill="none">
                <path
                  d="M3 3l18 18"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />
                <path
                  d="M10.4 10.4A2.8 2.8 0 0 0 12 15.2c.55 0 1.07-.16 1.5-.44"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M6.1 6.8C3.5 8.7 2.25 12 2.25 12s3.75 7.5 9.75 7.5c1.7 0 3.2-.3 4.5-.8"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M9.2 4.9c.9-.25 1.85-.4 2.8-.4 6 0 9.75 7.5 9.75 7.5s-1.45 2.9-4.2 5"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>

          <p id="password-help" class="help" :class="passwordError ? 'help-error' : 'help-muted'">
            <span v-if="passwordError">{{ passwordError }}</span>
            <span v-else>Minimum 8 characters.</span>
          </p>
        </div>

        <div v-if="apiError" class="api-error" role="alert">
          <div class="api-dot"></div>
          <div class="api-text">{{ apiError }}</div>
        </div>

        <button
          type="submit"
          :disabled="isLoggingIn"
          class="btn"
        >
          <span v-if="isLoggingIn" class="spinner" aria-hidden="true"></span>
          <span>{{ isLoggingIn ? 'Signing in…' : 'Login' }}</span>
        </button>

        <div class="footer">
          <span class="footer-muted">Don’t have an account?</span>
          <a href="/contactus" class="footer-link">Sign Up</a>
        </div>
      </form>
    </div>

    <div class="subfooter">
      <div class="subfooter-inner">
        <span class="sub-muted">Secure access • Encrypted sessions</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import LoadingOverlay from '@/views/LoadingOverlay.vue'

export default {
  name: 'Login',
  components: { LoadingOverlay },
  data() {
    return {
      account: { email: '', password: '' },
      emailError: '',
      passwordError: '',
      apiError: '',
      isLoggingIn: false,
      showMessage: true,
      messageTimeout: null,
      showPassword: false
    }
  },
  computed: {
    ...mapState(['isLoading']),
    message() {
      return this.$route.query.message
    }
  },
  methods: {
    closeMessage() {
      this.showMessage = false
    },
    validateEmail() {
      const re = /\S+@\S+\.\S+/
      this.emailError = re.test(this.account.email) ? '' : 'Enter a valid email address.'
    },
    validatePassword() {
      this.passwordError = this.account.password.length < 8 ? 'Password must be at least 8 characters.' : ''
    },
    async performLogin() {
      this.apiError = ''
      this.validateEmail()
      this.validatePassword()
      if (this.emailError || this.passwordError) return

      try {
        this.isLoggingIn = true
        this.$store.commit('setLoading', true)

        await this.$store.dispatch('auth/login', this.account)

        sessionStorage.removeItem('admin_customer_id')
        sessionStorage.removeItem('admin_mode')
        sessionStorage.removeItem('greeting_alert_dismissed')

        window.location.href = '/'
      } catch (error) {
        console.error('Login error:', error)
        this.apiError = 'Invalid login. Check your email/password and try again.'
      } finally {
        this.isLoggingIn = false
        this.$store.commit('setLoading', false)
      }
    }
  },
  created() {
    if (localStorage.getItem('token')) {
      this.$router.push('/')
    }
    if (this.message) {
      this.messageTimeout = setTimeout(() => (this.showMessage = false), 3500)
    }
  },
  beforeUnmount() {
    clearTimeout(this.messageTimeout)
  }
}
</script>

<style scoped>
.page {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 24px;
  position: relative;
  z-index: 1;
}

.backimg {
  position: fixed;
  inset: 0;
  z-index: 0;
  background:
    radial-gradient(1200px 600px at 50% 20%, rgba(59, 130, 246, 0.18), transparent 60%),
    radial-gradient(900px 500px at 70% 60%, rgba(244, 126, 64, 0.16), transparent 55%),
    linear-gradient(to bottom, rgba(255, 255, 255, 0.75), rgba(255, 255, 255, 0.92)),
    url('~@/assets/swirl_crop.jpg') center/cover no-repeat;
}

/* ===== Force the login panel to not be skinny (works for both layouts) ===== */

/* New layout */
.card {
  width: min(92vw, 480px) !important;
  max-width: 560px !important;
}

/* If you still have the old layout (content-container + form) */
.content-container form {
  width: min(92vw, 480px) !important;
  max-width: 480px !important;
}

/* If your form has max-w-sm still, this overrides it */
form.w-full.max-w-sm {
  max-width: 480px !important;
}

/* Give the inputs breathing room too */
@media (min-width: 560px) {
  .card-top,
  .form {
    padding-left: 28px !important;
    padding-right: 28px !important;
  }
}



.card-top {
  padding: 22px 22px 12px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.18);
}

.brand {
  display: flex;
  justify-content: center;
  padding-top: 6px;
}

.brand-logo {
  height: 72px;
  width: auto;
}

.title-wrap {
  text-align: center;
  margin-top: 10px;
}

.title {
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #0f172a;
}

.subtitle {
  margin-top: 6px;
  font-size: 13px;
  color: #475569;
}

.form {
  padding: 18px 22px 22px;
}

.field {
  margin-top: 14px;
}

.label-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.label {
  font-size: 13px;
  font-weight: 800;
  color: #334155;
}

.link {
  font-size: 12px;
  font-weight: 700;
  color: #2563eb;
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}

.control {
  margin-top: 8px;
}

.input {
  width: 100%;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.55);
  background: rgba(248, 250, 252, 0.85);
  padding: 12px 14px;
  font-size: 14px;
  color: #0f172a;
  outline: none;
  transition: box-shadow 160ms ease, border-color 160ms ease, background 160ms ease;
}

.input:focus {
  background: #ffffff;
  border-color: rgba(37, 99, 235, 0.55);
  box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.14);
}

.input:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}

.input-error {
  border-color: rgba(239, 68, 68, 0.6);
}

.input-error:focus {
  border-color: rgba(239, 68, 68, 0.75);
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.12);
}

.help {
  margin-top: 8px;
  font-size: 12px;
}

.help-muted {
  color: #64748b;
}

.help-error {
  color: #f47e40;
  font-weight: 700;
}

.password-wrap {
  position: relative;
}

.pw-toggle {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: transparent;
  padding: 6px;
  border-radius: 10px;
  color: #64748b;
  cursor: pointer;
}

.pw-toggle:hover {
  background: rgba(148, 163, 184, 0.16);
  color: #334155;
}

.pw-toggle:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.pw-icon {
  width: 18px;
  height: 18px;
}

.api-error {
  margin-top: 14px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
  border-radius: 14px;
  padding: 12px 12px;
  border: 1px solid rgba(244, 126, 64, 0.35);
  background: rgba(244, 126, 64, 0.10);
}

.api-dot {
  width: 10px;
  height: 10px;
  margin-top: 4px;
  border-radius: 999px;
  background: #f47e40;
  box-shadow: 0 0 0 4px rgba(244, 126, 64, 0.15);
}

.api-text {
  font-size: 13px;
  color: #7c2d12;
  font-weight: 700;
}

.btn {
  width: 100%;
  margin-top: 16px;
  border-radius: 14px;
  padding: 12px 14px;
  color: white;
  font-weight: 900;
  letter-spacing: 0.01em;
  border: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: linear-gradient(135deg, #f47e40, #ff9a5b);
  box-shadow:
    0 16px 30px rgba(244, 126, 64, 0.25),
    0 2px 10px rgba(15, 23, 42, 0.08);
  transition: transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow:
    0 18px 34px rgba(244, 126, 64, 0.30),
    0 2px 10px rgba(15, 23, 42, 0.10);
}

.btn:active {
  transform: translateY(0px);
}

.btn:disabled {
  opacity: 0.75;
  cursor: not-allowed;
  transform: none;
}

.spinner {
  width: 16px;
  height: 16px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: rgba(255, 255, 255, 1);
  animation: spin 700ms linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.footer {
  margin-top: 16px;
  display: flex;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
}

.footer-muted {
  color: #64748b;
}

.footer-link {
  color: #2563eb;
  font-weight: 900;
  text-decoration: none;
}

.footer-link:hover {
  text-decoration: underline;
}

.subfooter {
  margin-top: 18px;
  width: 100%;
  max-width: 440px;
  display: flex;
  justify-content: center;
  position: relative;
  z-index: 1;
}

.subfooter-inner {
  font-size: 12px;
  color: #64748b;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.20);
  backdrop-filter: blur(8px);
  padding: 8px 12px;
  border-radius: 999px;
}

.sub-muted {
  font-weight: 700;
}

.toast {
  position: fixed;
  top: 14px;
  right: 14px;
  z-index: 50;
  width: min(420px, calc(100vw - 28px));
}

.toast-inner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 12px;
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.88);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(148, 163, 184, 0.22);
  box-shadow: 0 18px 38px rgba(15, 23, 42, 0.28);
  color: #e2e8f0;
}

.toast-dot {
  width: 10px;
  height: 10px;
  margin-top: 6px;
  border-radius: 999px;
  background: #60a5fa;
  box-shadow: 0 0 0 4px rgba(96, 165, 250, 0.18);
}

.toast-title {
  font-size: 12px;
  font-weight: 900;
  color: #93c5fd;
  letter-spacing: 0.02em;
}

.toast-msg {
  font-size: 13px;
  font-weight: 700;
  color: #e2e8f0;
  margin-top: 2px;
}

.toast-close {
  margin-left: auto;
  border: 0;
  background: transparent;
  color: #cbd5e1;
  font-size: 22px;
  line-height: 1;
  padding: 2px 8px;
  border-radius: 10px;
  cursor: pointer;
}

.toast-close:hover {
  background: rgba(148, 163, 184, 0.14);
  color: #ffffff;
}

.toast-enter-active, .toast-leave-active {
  transition: opacity 220ms ease, transform 220ms ease;
}

.toast-enter-from, .toast-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
